import { createContext, useContext } from "react";

export const MIN_VISIBLE_MS = 900;
export const SAFETY_MS      = 30_000;

export function waitForNextPaint(): Promise<void> {
  return new Promise(resolve => {
    let settled = false;
    const settle = () => { if (!settled) { settled = true; resolve(); } };
    // Background-tab guard: rAF is throttled (or never fires) in hidden tabs.
    // Resolve after 100 ms max so callers are never permanently blocked.
    const timer = setTimeout(settle, 100);
    requestAnimationFrame(() => requestAnimationFrame(() => { clearTimeout(timer); settle(); }));
  });
}

export interface RheaLoadingCtx {
  /** Starts the loader with an optional message.
   *  Returns a `finish()` bound to this specific operation.
   *  A stale finish (from a previous call) is a no-op. */
  startLoading: (message?: string) => () => void;
  /** Force-hides the loader immediately without waiting for MIN_VISIBLE_MS.
   *  Invalidates any pending finish() token. Use when content is ready to show. */
  clearLoading: () => void;
  /** True while the full-screen Rhea loading overlay is on screen. Other
   *  loading surfaces (e.g. ColdStartBanner) read this to avoid duplicating
   *  marketplace loading feedback — only one surface should be visible at once. */
  isOverlayVisible: boolean;
}

export const RheaLoadingContext = createContext<RheaLoadingCtx>({
  startLoading: () => () => {},
  clearLoading: () => {},
  isOverlayVisible: false,
});

export function useRheaLoading(): RheaLoadingCtx {
  return useContext(RheaLoadingContext);
}
